'use client'

import { useState } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useLearningRecord } from '@/hooks/useLearningRecord'
import { useTranslation } from 'react-i18next';
import {
  R, PrintFrame, PrintButton, RecordHeader, StatRow,
  StandardStatement, SupervisorBlock, scoreTone, fmtDate,
  TranscriptControls, resolvePreset, inRange, fmtRange, FeedbackBlock, type PeriodRange,
  resolveRecordVariant, getRecordCopy,
} from './recordShared'

export default function TranscriptPage() {
  const { t } = useTranslation();

  const { childId } = useParams<{ childId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { userProfile } = useAuth()
  const { child, courses, feedback, loading, error } = useLearningRecord(childId)

  const isFaithSubject = Boolean(
    courses.length > 0 && courses.every(c => {
      const g = (c.subjectGroup || '').toLowerCase()
      return g.includes('bible') || g.includes('theology') || g.includes('faith') || g.includes('church') || g.includes('ministry')
    })
  )

  const variant = resolveRecordVariant({
    searchContext: searchParams.get('context'),
    searchVariant: searchParams.get('variant'),
    childOrgType: child?.orgType,
    childFacilityType: child?.facilityType,
    userOrgType: (userProfile as any)?.facility_org_type,
    userFacilityType: userProfile?.facility_type,
    isFaithSubject,
  })

  const copy = getRecordCopy(variant, child?.orgName)
  const feedbackLabel = copy.feedbackLabel

  const backTarget = variant === 'church'
    ? '/church/learn'
    : (!childId || childId === 'me') ? '/learn' : `/homeschool/child/${childId}`

  const [preset, setPreset] = useState('all')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())   // empty = all

  const range: PeriodRange = preset === 'custom'
    ? { label: 'Custom Range',
        from: customFrom ? new Date(customFrom + 'T00:00:00') : null,
        to: customTo ? new Date(customTo + 'T23:59:59') : null }
    : resolvePreset(preset)
  const isAllTime = !range.from && !range.to

  if (loading) {
    return <div style={{ minHeight: '100dvh', background: '#ECE9E1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: R.slate }}>
      Loading transcript…
    </div>
  }

  if (error || !child || courses.length === 0) {
    return (
      <PrintFrame maxWidth={640}>
        <RecordHeader eyebrow={copy.eyebrow} title="Transcript unavailable" />
        <p style={{ color: R.slate, fontSize: 14 }}>{error || 'No enrolled courses with records found for this student.'}</p>
        <Link to={backTarget} className="no-print"
          style={{ display: 'inline-block', marginTop: 16, color: R.gold, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
          ← Back
        </Link>
      </PrintFrame>
    )
  }

  // ── Period + course filtering ────────────────────────────────────────────
  // Mastery is a rolling CURRENT value — period stats are recomputed from evidence
  // (lessons completed in period, quiz scores in period), current mastery stays labeled.
  const selectionActive = selected.size > 0
  const visibleCourses = courses
    .filter(c => !selectionActive || selected.has(c.classId))
    .map(c => {
      const periodLessons = isAllTime
        ? c.lessons
        : c.lessons.filter(l => inRange(l.quiz?.lastAt ?? l.log?.completedAt ?? null, range))
      const periodQuizzes = periodLessons.filter(l => l.quiz).map(l => l.quiz!.best)
      return {
        ...c,
        periodLessons,
        periodCompleted: periodLessons.filter(l => l.completed).length,
        periodAvg: periodQuizzes.length
          ? Math.round(periodQuizzes.reduce((s, x) => s + x, 0) / periodQuizzes.length)
          : null,
      }
    })
    .filter(c => isAllTime || c.periodLessons.length > 0)   // period view: omit silent courses

  const omittedCount = courses.length - visibleCourses.length

  const byGroup = new Map<string, typeof visibleCourses>()
  for (const c of visibleCourses) {
    const arr = byGroup.get(c.subjectGroup) || []
    arr.push(c)
    byGroup.set(c.subjectGroup, arr)
  }

  return (
    <PrintFrame>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <button onClick={() => navigate(backTarget)}
          style={{ background: 'none', border: 'none', color: R.slate, fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
          ← Back
        </button>
        <PrintButton label={t('common.print_transcript_pdf')} />
      </div>

      <TranscriptControls
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        courses={courses.map(c => ({ classId: c.classId, className: c.className }))}
        selected={selected} setSelected={setSelected}
      />

      <RecordHeader
        eyebrow={copy.eyebrow}
        title={child.name}
        subtitle={`${copy.transcriptSubtitle}${child.age && variant === 'homeschool' ? ` · Age ${child.age}` : ''} · ${visibleCourses.length} course${visibleCourses.length === 1 ? '' : 's'} · Period: ${fmtRange(range)}`}
        right={<PrintButton label={t('common.print_transcript_pdf')} />}
      />

      <StatRow items={[
        { label: 'Courses', value: visibleCourses.length },
        { label: 'Completed', value: visibleCourses.filter(c => c.completedOn).length },
        { label: 'Lessons in period', value: visibleCourses.reduce((s, c) => s + c.periodCompleted, 0) },
        { label: 'Period quiz avg', value: (() => {
            const qs = visibleCourses.flatMap(c => c.periodLessons.filter(l => l.quiz).map(l => l.quiz!.best))
            return qs.length ? `${Math.round(qs.reduce((s, x) => s + x, 0) / qs.length)}%` : '—'
          })() },
      ]} />

      {[...byGroup.entries()].map(([group, list]) => (
        <div key={group} style={{ marginBottom: 18 }}>
          <div className="module-header"
            style={{ fontWeight: 800, fontSize: 13, color: R.gold, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
            {group}
          </div>
          {list.map(c => {
            return (
              <div key={c.classId} className="print-block"
                style={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 90px', gap: 10,
                          padding: '8px 12px', borderBottom: `1px solid ${R.border}`, fontSize: 12.5, alignItems: 'center' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: R.ink }}>{c.className}</div>
                  <div style={{ fontSize: 10.5, color: R.muted }}>
                    {isAllTime
                      ? `${c.plan.length} modules · ${c.planLessons} lessons · ${c.planConcepts} concepts · Current mastery ${Math.round(c.mastery)}%`
                      : `${c.periodCompleted} lessons · ${c.periodAvg !== null ? `${c.periodAvg}% avg` : 'no quizzes'} this period · current mastery ${Math.round(c.mastery)}%`}
                    {c.dateRange.first ? ` · ${fmtDate(c.dateRange.first)} – ${fmtDate(c.dateRange.last)}` : ''}
                  </div>
                </div>
                <span style={{ textAlign: 'right', fontWeight: 800, color: scoreTone(c.mastery), fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(c.mastery)}%
                </span>
                <span style={{ textAlign: 'right', color: R.slate, fontVariantNumeric: 'tabular-nums' }}>
                  {isAllTime ? `${c.lessons.filter(l => l.completed).length}/${c.planLessons}` : `${c.periodCompleted} done`}
                </span>
                <span style={{ textAlign: 'right', fontSize: 11, color: c.completedOn ? R.green : R.slate }}>
                  {c.completedOn ? `Completed ${fmtDate(c.completedOn)}` : c.periodCompleted > 0 ? 'In progress' : 'Not started'}
                </span>
              </div>
            )
          })}
        </div>
      ))}

      {!isAllTime && omittedCount > 0 && (
        <p style={{ fontSize: 10.5, color: R.muted, margin: '12px 0 0' }}>
          {omittedCount} course{omittedCount === 1 ? '' : 's'} had no recorded activity in this period and {omittedCount === 1 ? 'is' : 'are'} omitted from this report.
        </p>
      )}

      {/* Per-course detail note */}
      <p style={{ fontSize: 11, color: R.muted, margin: '20px 0 0' }}>
        {copy.notesPrompt}
      </p>

      {/* Human feedback — teacher overrides + parent encouragement, printed on transcript */}
      {feedback.length > 0 && (
        <FeedbackBlock rows={feedback} headerLabel={feedbackLabel} />
      )}

      <StandardStatement variant={variant} orgName={child?.orgName} />
      <SupervisorBlock variant={variant} orgName={child?.orgName} />
      <p style={{ marginTop: 28, textAlign: 'center', fontSize: 10.5, color: R.muted }}>
        {copy.footer}
      </p>
    </PrintFrame>
  )
}